import { format, getMonth, getYear, parseISO, startOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ExpenseCategoryDatabase, ExpenseDatabase } from '../../../types';

export const MONTH_LABELS = Array.from({ length: 12 }, (_, month) => (
  format(new Date(2000, month, 1), 'MMM', { locale: fr })
));

export function getExpenseYear(expense: Pick<ExpenseDatabase, 'month'>) {
  return getYear(parseISO(expense.month));
}

export function formatExpenseMonth(month: string) {
  return format(parseISO(month), 'MMMM yyyy', { locale: fr });
}

/** `yyyy-MM-dd` of the first day of the month, in local time. */
export function toExpenseMonth(date: Date) {
  return format(startOfMonth(date), 'yyyy-MM-dd');
}

/** Years having at least one expense, latest first, always including the current year. */
export function getExpenseYears(expenses: Pick<ExpenseDatabase, 'month'>[], currentYear: number) {
  const years = new Set(expenses.map(getExpenseYear));
  years.add(currentYear);

  return [...years].sort((a, b) => b - a);
}

export type CategoryTotal = {
  categoryId: string;
  name: string;
  total: number;
  /** Share of the year's total, between 0 and 1. */
  share: number;
};

/** Total spent per category over a year, largest first; categories without expense are left out. */
export function getCategoryTotals(
  expenses: ExpenseDatabase[],
  categories: ExpenseCategoryDatabase[],
  year: number,
): CategoryTotal[] {
  const yearExpenses = expenses.filter((expense) => getExpenseYear(expense) === year);
  const grandTotal = yearExpenses.reduce((sum, expense) => sum + expense.amount, 0);

  return categories
    .map((category) => {
      const total = yearExpenses
        .filter((expense) => expense.category_id === category.id)
        .reduce((sum, expense) => sum + expense.amount, 0);

      return {
        categoryId: category.id,
        name: category.name,
        total,
        share: grandTotal > 0 ? total / grandTotal : 0,
      };
    })
    .filter((item) => item.total > 0)
    .sort((a, b) => b.total - a.total);
}

/** Twelve monthly amounts of one category over a year; `null` for a month without expense. */
export function getMonthlyAmounts(expenses: ExpenseDatabase[], categoryId: string, year: number) {
  return Array.from({ length: 12 }, (_, month) => {
    const monthExpenses = expenses.filter((expense) => (
      expense.category_id === categoryId
      && getExpenseYear(expense) === year
      && getMonth(parseISO(expense.month)) === month
    ));

    return monthExpenses.length === 0
      ? null
      : monthExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  });
}

export type YearTotal = {
  year: number;
  total: number;
  /** Variation against the previous year (0.1 for +10 %), `null` without a previous amount. */
  variation: number | null;
};

/** Yearly totals from the latest year to the oldest one, years without expense included. */
export function getYearTotals(expenses: ExpenseDatabase[]): YearTotal[] {
  if (expenses.length === 0) {
    return [];
  }

  const years = expenses.map(getExpenseYear);
  const oldestYear = Math.min(...years);
  const latestYear = Math.max(...years);
  const totals: YearTotal[] = [];

  for (let year = oldestYear; year <= latestYear; year++) {
    const total = expenses
      .filter((expense) => getExpenseYear(expense) === year)
      .reduce((sum, expense) => sum + expense.amount, 0);
    const previous = totals[totals.length - 1];

    totals.push({
      year,
      total,
      variation: previous && previous.total > 0 ? (total - previous.total) / previous.total : null,
    });
  }

  return totals.reverse();
}
